// src/utils/eventDateTime.js
// Event times are FLOATING WALL-CLOCK, local to the locality where the event happens.
// A 19:00 event is 19:00 in that place and must never shift based on the viewer's timezone.
//
// They are stored in event_details.start_date / end_date / rsvp_deadline as
// `timestamp without time zone` — naked "YYYY-MM-DDTHH:mm[:ss]" strings with no offset.
//
// `new Date("2026-07-15T19:00:00")` is NOT safe for these values: the JS engine may
// reinterpret an offset-less string as UTC (date-only forms always do; some engines/formats
// vary), which shifts the displayed time by the viewer's offset. parseEventDate reads the
// calendar parts explicitly and builds a LOCAL Date, so the wall-clock is preserved verbatim.

export function parseEventDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const m = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!m) {
    // Unrecognised shape — fall back, but never return an Invalid Date.
    const fallback = new Date(value);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  const [, y, mo, d, h = 0, mi = 0, s = 0] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
}

// Has this event ENDED? Times are floating wall-clock — always compare via parseEventDate.
// Future = not ended, Past = ended; every dated event falls on one side. An event with no
// start_date is treated as not ended.
//   - has end_date -> ended once end_date is in the past (keeps multi-day events live to the end)
//   - no end_date  -> ended once its start day has fully passed (compare to today-at-midnight)
// (Which events reach a list, and when display_from releases one, is decided by the feed itself —
// get_articles_by_locality_v2, 20261018b_feed_dam_ranking_events.sql — not here.)
export function hasEventEnded(event, now = new Date()) {
  if (!event || !event.start_date) return false;
  if (event.end_date) {
    return parseEventDate(event.end_date) < now;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parseEventDate(event.start_date) < today;
}
